const HIGHLIGHT_SPAN =
  '<span class="keyword-highlight" style="background-color: #ffeb3b; padding: 0 2px; border-radius: 2px; display: inline; line-height: 1.2; vertical-align: baseline;">'

/**
 * Generates abbreviation variants for a keyword (e.g., "us" → ["us", "US", "U.S."])
 */
function generateAbbreviationVariants(word: string): string[] {
  const variants = new Set<string>([word])

  // Convert to uppercase if it's a short word (likely abbreviation)
  if (word.length <= 3 && /^[a-z]+$/i.test(word)) {
    const upper = word.toUpperCase()
    variants.add(upper)

    // Add period variant (e.g., "US" → "U.S.")
    if (upper.length >= 2 && upper.length <= 3) {
      const withPeriods = upper.split('').join('.') + '.'
      variants.add(withPeriods)
    }
  }

  return Array.from(variants)
}

/**
 * Highlights keywords in HTML content using two-pass approach: phrases first (with dash variants), then individual words. Prevents partial matches and highlighting in HTML attributes/tags.
 */
export function highlightKeywordsInHtml(htmlContent: string, keywords: string[]): string {
  if (!htmlContent || !keywords?.length) return htmlContent

  // Filter and trim valid keywords
  const validKeywords = keywords.map((k) => k?.trim()).filter((k) => k.length > 0)

  if (!validKeywords.length || htmlContent.includes('keyword-highlight')) {
    return htmlContent
  }

  // Separate multi-word phrases from single words
  const phraseKeywords: string[] = []
  const singleWordKeywords = new Set<string>()

  validKeywords.forEach((keyword) => {
    if (keyword.includes(' ')) {
      phraseKeywords.push(keyword)
      // Add individual words for highlighting when not part of phrases
      keyword.split(/\s+/).forEach((word) => word && singleWordKeywords.add(word))
    } else {
      singleWordKeywords.add(keyword)
    }
  })

  // Check if match is inside HTML tags/attributes to skip highlighting
  const shouldSkip = (offset: number, matchLength: number, string: string): boolean => {
    const beforeMatch = string.substring(Math.max(0, offset - 300), offset)
    const afterMatch = string.substring(offset + matchLength, Math.min(string.length, offset + matchLength + 100))
    const lastOpenTag = beforeMatch.lastIndexOf('<')
    const lastCloseTag = beforeMatch.lastIndexOf('>')
    const isInsideTag = lastOpenTag > lastCloseTag

    if (!isInsideTag) return false

    // Do not wrap HTML tag names (e.g. keyword "a" matching `<a>` / `</a>`)
    if (/<\/?[a-z0-9]*$/i.test(beforeMatch)) return true

    // Check if inside attribute quotes or followed by URL/attribute indicators
    const beforeQuotes = beforeMatch.substring(lastOpenTag > -1 ? lastOpenTag : 0)
    const isInQuotes = (beforeQuotes.match(/["']/g) || []).length % 2 === 1
    const immediatelyAfter = string.substring(offset + matchLength, offset + matchLength + 1)
    const isFollowedBySlash = immediatelyAfter === '/'
    const afterContext = afterMatch.substring(0, 30)
    const isFollowedByAttribute = /^[\s]*\/["']|^[\s]*\/>|^[\s]*["']|^[\s]*=/.test(afterContext.trim())

    return isInQuotes || isFollowedBySlash || isFollowedByAttribute
  }

  let result = htmlContent

  // Pass 1: Highlight multi-word phrases (matches space, hyphen, en dash, em dash)
  if (phraseKeywords.length > 0) {
    const phrasePatterns: string[] = []

    phraseKeywords.forEach((keyword) => {
      const words = keyword.split(/\s+/)

      // Generate variants for each word (handles abbreviations like "us" → "US" → "U.S.")
      const wordVariants = words.map((word) => {
        const variants = generateAbbreviationVariants(word)
        // Escape each variant and combine with OR
        const escaped = variants.map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        return `(?:${escaped.join('|')})`
      })

      // Create pattern that matches variants with flexible separators
      // Matches: space, hyphen, en dash, em dash, or period-dash (e.g., "U.S.-focused")
      // Pattern: (word1 variants) [separator] (word2 variants) [separator] ...
      // Separator can be: space/hyphen/dash OR period followed by dash (no space between period and dash)
      const separatorPattern = `(?:[\\s\\-–—]+|\\.[\\-–—]+)`
      const pattern = `(?<![\\w])${wordVariants.join(separatorPattern)}(?![\\w])`
      phrasePatterns.push(pattern)
    })

    // Sort by word count (longest first) to prioritize longer phrases
    phrasePatterns.sort((a, b) => {
      const aWords = (a.match(/\\w+/g) || []).length
      const bWords = (b.match(/\\w+/g) || []).length
      return bWords - aWords
    })

    const phrasePattern = new RegExp(`(${phrasePatterns.join('|')})`, 'giu')
    result = result.replace(phrasePattern, (match, _, offset, string) => {
      if (shouldSkip(offset, match.length, string)) return match
      // Highlight complete phrase as a single unit (handles spaces, dashes, and special characters)
      return `${HIGHLIGHT_SPAN}${match}</span>`
    })
  }

  // Pass 2: Highlight individual words (skip if already inside a highlight from pass 1)
  if (singleWordKeywords.size > 0) {
    const wordPatterns = Array.from(singleWordKeywords)
      .sort((a, b) => b.length - a.length) // Sort by length (longest first)
      .map((keyword) => {
        const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        // Word boundaries prevent partial matches (e.g., "power" won't match "powered")
        return `(?<![\\w])${escaped}(?![\\w])`
      })

    const wordPattern = new RegExp(`(${wordPatterns.join('|')})`, 'giu')
    result = result.replace(wordPattern, (match, _, offset, string) => {
      // Skip if already inside a highlighted span from pass 1
      const beforeMatch = string.substring(Math.max(0, offset - 200), offset)
      const lastSpanOpen = beforeMatch.lastIndexOf('<span class="keyword-highlight"')
      const lastSpanClose = beforeMatch.lastIndexOf('</span>')
      if (lastSpanOpen > lastSpanClose || shouldSkip(offset, match.length, string)) return match
      return `${HIGHLIGHT_SPAN}${match}</span>`
    })
  }

  return result
}

/**
 * Extracts and splits keywords from Next.js router query object (comma-separated values)
 */
export function extractKeywordsFromQuery(query: any): string[] {
  const keyword = query?.keyword as string
  if (!keyword) return []
  return keyword
    .split(',')
    .map((k) => k.trim())
    .filter((k) => k.length > 0)
}

/**
 * Checks if keyword highlighting should be applied based on query parameters (validates keyword exists and is non-empty string)
 */
export function shouldHighlightKeywords(query: any): boolean {
  return !!(query?.keyword && typeof query.keyword === 'string' && query.keyword.trim().length > 0)
}
